import { Children, cloneElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./OsseDashboard.module.css";
import {
  MISSING, buildDataset, isInfoMarker, parseCSV, recordFor, toISO, toNum, valueFor, valuesOf,
} from "../../utils/osseExport";

// OSSE Dashboard — evaluates an OSSE register CSV export in the browser (the file is
// never uploaded). Same counting rules as the MOS Dashboard: one answer per patient
// (the newest filled-in form), empty answers count as "Keine Antwort".
// Charts receive "vals": one array of answers per patient ([] = no answer).

const MAX_FILE_MB = 10;
const COLORS = ["#0ba1e2", "#10b981", "#f59e0b", "#8b5cf6", "#ef4444", "#06b6d4", "#84cc16", "#f97316", "#ec4899", "#6366f1"];
const NO_ANSWER_LABEL = "Keine Antwort";
const NO_ANSWER_COLOR = "#cbd5e1";
const ROMAN = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X", "XI", "XII"];
const MONTHS = ["Jan.", "Feb.", "März", "Apr.", "Mai", "Juni", "Juli", "Aug.", "Sep.", "Okt.", "Nov.", "Dez."];
const BAR_LIMIT = 10;
// Questions not charted in the overview (still listed in "Alle Antworten")
const HIDDEN_IN_OVERVIEW = [/^KI\s*[–-]\s*Auswertung des Bogens$/i];
// Number questions shown as ranges of equal width from 0 to max (lower bound included,
// upper bound excluded; max itself belongs to the last range). Only ranges with answers are shown.
const RANGE_CHARTS = [
  { match: /Tragezeit.*Stunden/i, unit: "h", step: 1, max: 24 },
];

const fmtNum = (n, d = 1) => (n == null || isNaN(n) ? "–" : n.toLocaleString("de-DE", { maximumFractionDigits: d }));
const fmtDate = (iso) => (iso ? iso.split("-").reverse().join(".") : "–");
const pct = (a, b) => (b ? ((a / b) * 100).toFixed(1) : "0.0");
const shortName = (name) => name.split(" – ").pop();

const STATUS_LABEL = { open: "Offen", reported: "Gemeldet", completed: "Abgeschlossen", validated: "Validiert" };
const statusText = (v) => (v === MISSING ? "Nicht begonnen" : STATUS_LABEL[v.toLowerCase()] || v);
const statusRank = (v) => (/report|complet|valid|sign|abgeschl|fertig/i.test(v) ? 0 : /open|offen|progress|bearb/i.test(v) ? 1 : 2);

async function readFileText(file) {
  const buf = await file.arrayBuffer();
  try { return new TextDecoder("utf-8", { fatal: true }).decode(buf); }
  catch { return new TextDecoder("windows-1252").decode(buf); }
}

const numOf = (v) => (isInfoMarker(v) ? null : toNum(v));
const isoOf = (v) => (isInfoMarker(v) ? null : toISO(v));

// Several answers of one patient (multiple choice) form one combined answer
// ("Dorsal + Plantar"), so every patient is counted exactly once.
const combine = (list) => (list.length > 1 ? [[...list].sort((a, b) => a.localeCompare(b, "de")).join(" + ")] : list);

// Counts per answer (patients naming it); numeric-looking answers ascending, others by frequency
function answerCounts(f, vals) {
  const m = new Map();
  for (const list of vals) for (const v of list) m.set(v, (m.get(v) || 0) + 1);
  const entries = [...m.entries()];
  if (f.type === "status") return entries.sort((a, b) => statusRank(a[0]) - statusRank(b[0]) || b[1] - a[1]);
  const numeric = (v) => toNum(v) != null;
  return entries.sort((a, b) =>
    numeric(a[0]) && numeric(b[0]) ? toNum(a[0]) - toNum(b[0])
      : numeric(a[0]) !== numeric(b[0]) ? (numeric(a[0]) ? -1 : 1)
      : b[1] - a[1] || a[0].localeCompare(b[0]));
}

// ── Chart building blocks (same look as the MOS Dashboard) ─────────────────

const cardClass = (wide) => `${styles.chartCard}${wide ? " " + styles.chartWide : ""}`;

function Card({ title, total, wide, children }) {
  return (
    <div className={cardClass(wide)}>
      <p className={styles.chartTitle}>{title}</p>
      <p className={styles.chartMeta}>n = {total}</p>
      {children}
    </div>
  );
}

function NoAnswerLine({ noAnswer, total, others }) {
  const parts = [];
  others.forEach(([v, c]) => parts.push(`${v}: ${c}`));
  if (noAnswer) parts.push(`${NO_ANSWER_LABEL}: ${noAnswer} (${pct(noAnswer, total)}%)`);
  if (!parts.length) return null;
  return <p className={styles.cardFoot}>{parts.join(" · ")}</p>;
}

// Horizontal bars: label · bar · count (percentage of all counted patients)
function Bars({ entries, total }) {
  const max = Math.max(...entries.map((e) => e.count), 1);
  return (
    <div className={styles.barList}>
      {entries.map((e) => (
        <div key={e.value} className={styles.barRow}>
          <div className={styles.barLabelWrap}>
            <span className={`${styles.barLabel}${e.muted ? " " + styles.mutedLabel : ""}`}>{e.label}</span>
          </div>
          <div className={styles.barTrack}>
            <div className={styles.barFill} style={{ width: `${(e.count / max) * 100}%`, background: e.color }} />
          </div>
          <span className={styles.barValue}>{e.count} <span className={styles.barPct}>({pct(e.count, total)}%)</span></span>
        </div>
      ))}
    </div>
  );
}

function BarChart({ title, f, vals, wide = false }) {
  const [showAll, setShowAll] = useState(false);
  const total = vals.length;
  const noAnswer = vals.filter((l) => !l.length).length;
  const all = answerCounts(f, vals).map(([v, c], i) => ({
    value: v, label: f.type === "status" ? statusText(v) : v, count: c, color: COLORS[i % COLORS.length],
  }));
  const entries = (showAll ? all : all.slice(0, BAR_LIMIT)).slice();
  if (noAnswer > 0) entries.push({ value: MISSING, label: NO_ANSWER_LABEL, count: noAnswer, color: NO_ANSWER_COLOR, muted: true });
  return (
    <Card title={title} total={total} wide={wide}>
      <Bars entries={entries} total={total} />
      {all.length > BAR_LIMIT && (
        <button type="button" className={styles.moreBtn} onClick={() => setShowAll((s) => !s)}>
          {showAll ? "Weniger anzeigen" : `Alle ${all.length} Antworten anzeigen`}
        </button>
      )}
    </Card>
  );
}

function Columns({ buckets, axisL, axisR }) {
  const max = Math.max(...buckets.map((b) => b.c), 1);
  return (
    <>
      <div className={styles.scaleGrid}>
        {buckets.map((b) => (
          <div key={b.label} className={styles.scaleCol} title={`${b.label}: ${b.c}`}>
            <span className={styles.scaleCount}>{b.c || ""}</span>
            <div className={styles.scaleBar} style={{ height: Math.round((b.c / max) * 60) || 2, background: b.c ? "#0ba1e2" : "#e5eef4" }} />
          </div>
        ))}
      </div>
      <div className={styles.scaleEnds}><span>{axisL}</span><span>{axisR}</span></div>
    </>
  );
}

// Answers that are not a number/date (e.g. "Keine Informationen verfügbar") with counts
const otherAnswers = (vals, isValid) => {
  const m = new Map();
  for (const list of vals) for (const v of list) if (!isValid(v)) m.set(v, (m.get(v) || 0) + 1);
  return [...m.entries()];
};

function niceStep(x) {
  const p = Math.pow(10, Math.floor(Math.log10(x))), r = x / p;
  return (r <= 1 ? 1 : r <= 2 ? 2 : r <= 2.5 ? 2.5 : r <= 5 ? 5 : 10) * p;
}

function NumericChart({ title, vals, wide = false }) {
  const total = vals.length;
  const noAnswer = vals.filter((l) => !l.length).length;
  const v = vals.flat().map(numOf).filter((x) => x != null).sort((a, b) => a - b);
  const others = otherAnswers(vals, (x) => numOf(x) != null);
  if (!v.length) {
    return <Card title={title} total={total} wide={wide}><p className={styles.noData}>Keine Zahlenwerte</p><NoAnswerLine noAnswer={noAnswer} total={total} others={others} /></Card>;
  }
  const mean = v.reduce((a, b) => a + b, 0) / v.length;
  const med = v.length % 2 ? v[(v.length - 1) / 2] : (v[v.length / 2 - 1] + v[v.length / 2]) / 2;
  const mn = v[0], mx = v[v.length - 1];
  const step = niceStep((mx - mn || 1) / 8), start = Math.floor(mn / step) * step;
  const k = Math.max(1, Math.ceil((mx - start) / step + 1e-9));
  const buckets = [];
  for (let j = 0; j < k; j++) {
    const lo = start + j * step, hi = lo + step;
    buckets.push({ label: `${fmtNum(lo, 2)}–${fmtNum(hi, 2)}`, c: v.filter((y) => y >= lo && (y < hi || (j === k - 1 && y <= hi))).length });
  }
  return (
    <Card title={title} total={total} wide={wide}>
      <div className={styles.statRow}>
        <div><span className={styles.statLabel}>Mittelwert</span><span className={styles.statValue}>{fmtNum(mean)}</span></div>
        <div><span className={styles.statLabel}>Median</span><span className={styles.statValue}>{fmtNum(med)}</span></div>
        <div><span className={styles.statLabel}>Min.</span><span className={styles.statValue}>{fmtNum(mn, 2)}</span></div>
        <div><span className={styles.statLabel}>Max.</span><span className={styles.statValue}>{fmtNum(mx, 2)}</span></div>
      </div>
      <Columns buckets={buckets} axisL={buckets[0].label} axisR={buckets[buckets.length - 1].label} />
      <NoAnswerLine noAnswer={noAnswer} total={total} others={others} />
    </Card>
  );
}

// Number question as fixed ranges: one bar per range that has answers
function RangeChart({ title, vals, config, wide = false }) {
  const total = vals.length;
  const noAnswer = vals.filter((l) => !l.length).length;
  const v = vals.flat().map(numOf).filter((x) => x != null);
  const others = otherAnswers(vals, (x) => numOf(x) != null);
  const { step, max, unit } = config;
  const entries = [];
  for (let from = 0; from < max; from += step) {
    const to = from + step, last = to >= max;
    const count = v.filter((x) => x >= from && (x < to || (last && x <= max))).length;
    if (count) entries.push({ value: `r${from}`, label: `${from} – ${to} ${unit}`, count, color: COLORS[0] });
  }
  const outside = v.filter((x) => x < 0 || x > max).length;
  if (outside) entries.push({ value: "out", label: `außerhalb 0 – ${max} ${unit}`, count: outside, color: NO_ANSWER_COLOR, muted: true });
  others.forEach(([o, c]) => entries.push({ value: "o:" + o, label: o, count: c, color: NO_ANSWER_COLOR, muted: true }));
  if (noAnswer > 0) entries.push({ value: MISSING, label: NO_ANSWER_LABEL, count: noAnswer, color: NO_ANSWER_COLOR, muted: true });
  return (
    <Card title={title} total={total} wide={wide}>
      <Bars entries={entries} total={total} />
    </Card>
  );
}

function DateChart({ title, vals, wide = false }) {
  const total = vals.length;
  const noAnswer = vals.filter((l) => !l.length).length;
  const d = vals.flat().map(isoOf).filter(Boolean).sort();
  const others = otherAnswers(vals, (x) => !!isoOf(x));

  // One table cell per month (all dates within 3 years) or per year, chronological,
  // only for months/years that occur. Cells wrap into several columns.
  const ym = (s) => +s.slice(0, 4) * 12 + +s.slice(5, 7) - 1;
  const monthly = d.length > 0 && ym(d[d.length - 1]) - ym(d[0]) <= 36;
  const counts = new Map();
  for (const s of d) {
    const k = monthly ? ym(s) : +s.slice(0, 4);
    counts.set(k, (counts.get(k) || 0) + 1);
  }
  const entries = [...counts].sort((a, b) => a[0] - b[0]).map(([k, c]) => ({
    value: String(k),
    label: monthly ? `${MONTHS[k % 12]} ${Math.floor(k / 12)}` : String(k),
    count: c,
  }));
  others.forEach(([v, c]) => entries.push({ value: "o:" + v, label: v, count: c, muted: true }));
  if (noAnswer > 0) entries.push({ value: MISSING, label: NO_ANSWER_LABEL, count: noAnswer, muted: true });

  return (
    <Card title={title} total={total} wide={wide}>
      {entries.length ? (
        <>
          <div className={styles.dateHead}>{monthly ? "Monat" : "Jahr"} · Anzahl · Anteil</div>
          <div className={styles.dateGrid}>
            {entries.map((e) => (
              <div key={e.value} className={styles.dateCell}>
                <span className={`${styles.dateLabel}${e.muted ? " " + styles.mutedLabel : ""}`}>{e.label}</span>
                <span className={styles.dateCount}>{e.count}</span>
                <span className={styles.datePct}>{pct(e.count, total)}%</span>
              </div>
            ))}
          </div>
        </>
      ) : <p className={styles.noData}>Keine Datumsangaben</p>}
    </Card>
  );
}

function TextChart({ title, f, vals, wide = false }) {
  const [showAll, setShowAll] = useState(false);
  const entries = answerCounts(f, vals);
  const shown = showAll ? entries : entries.slice(0, 5);
  return (
    <Card title={title} total={vals.length} wide={wide}>
      <ul className={styles.textList}>
        {shown.map(([v, c]) => <li key={v}><span>{v}</span><span className={styles.barPct}>{c > 1 ? `×${c}` : ""}</span></li>)}
      </ul>
      {entries.length > 5 && (
        <button type="button" className={styles.moreBtn} onClick={() => setShowAll((s) => !s)}>
          {showAll ? "Weniger anzeigen" : `Alle ${entries.length} Antworten anzeigen`}
        </button>
      )}
      <NoAnswerLine noAnswer={vals.filter((l) => !l.length).length} total={vals.length} others={[]} />
    </Card>
  );
}

// Two-column grid: a card left alone in its row is stretched across both columns
function ChartGrid({ children }) {
  const items = Children.toArray(children);
  return (
    <div className={styles.grid}>
      {items.map((c, i) => (items.length % 2 === 1 && i === items.length - 1 ? cloneElement(c, { wide: true }) : c))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function OsseDashboard() {
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [ds, setDs]               = useState(null);
  const [fileName, setFileName]   = useState("");
  const [error, setError]         = useState("");
  const [location, setLocation]   = useState(""); // "" = all locations
  const [tab, setTab]             = useState("overview");
  const [answersForm, setAnswersForm] = useState(""); // form shown in "Alle Antworten"
  const [tlPage, setTlPage]       = useState(0);      // page of the "Rezidive" tab
  const [page, setPage]           = useState(0);
  const [pageSize, setPageSize]   = useState(25);
  const [dragging, setDragging]   = useState(false);

  const loadFile = useCallback(async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { setError(`„${file.name}“ ist keine CSV-Datei. Bitte den CSV-Export der Registerplattform wählen.`); return; }
    if (file.size > MAX_FILE_MB * 1024 * 1024) { setError(`„${file.name}“ ist größer als ${MAX_FILE_MB} MB.`); return; }
    try {
      const rows = parseCSV(await readFileText(file));
      if (rows.length < 2) throw new Error("Die Datei hat weniger als zwei Zeilen.");
      const built = buildDataset(rows);
      if (!built.n) throw new Error("Unter der Kopfzeile wurden keine Datenzeilen gefunden.");
      setDs(built); setFileName(file.name); setError("");
      setLocation(""); setTab("overview"); setAnswersForm(""); setPage(0); setTlPage(0);
    } catch (e) {
      setError(`„${file.name}“ konnte nicht gelesen werden: ${e.message} Bitte prüfen Sie, ob es sich um den CSV-Export der Registerplattform handelt.`);
    }
  }, []);

  // Drag & drop anywhere on the page
  useEffect(() => {
    let depth = 0;
    const enter = (e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); depth++; setDragging(true); } };
    const leave = () => { depth = Math.max(0, depth - 1); if (!depth) setDragging(false); };
    const over  = (e) => e.preventDefault();
    const drop  = (e) => { e.preventDefault(); depth = 0; setDragging(false); loadFile(e.dataTransfer?.files?.[0]); };
    window.addEventListener("dragenter", enter); window.addEventListener("dragleave", leave);
    window.addEventListener("dragover", over);   window.addEventListener("drop", drop);
    return () => {
      window.removeEventListener("dragenter", enter); window.removeEventListener("dragleave", leave);
      window.removeEventListener("dragover", over);   window.removeEventListener("drop", drop);
    };
  }, [loadFile]);

  // Patients of the selected location (all patients when no location is selected)
  const active = useMemo(() => {
    if (!ds) return [];
    if (!location || !ds.location) return ds.patients;
    return ds.patients.filter((p) => valueFor(ds, ds.location, p) === location);
  }, [ds, location]);
  const forms = ds ? ds.groups.filter((g) => g.id !== "_id") : [];

  // Chart of question f over the newest form groupId of each patient (default: f's own form)
  const renderChart = (f, groupId = f.group) => {
    const vals = active.map((p) => recordFor(ds, groupId, p)).filter(Boolean).map((r) => valuesOf(f, r.rows));
    const title = f.label;
    const range = f.type !== "text" && f.type !== "date" && RANGE_CHARTS.find((r) => r.match.test(f.label));
    if (range) return <RangeChart key={f.key} title={title} vals={vals} config={range} />;
    if (f.type === "numeric") return <NumericChart key={f.key} title={title} vals={vals} />;
    if (f.type === "date")    return <DateChart key={f.key} title={title} vals={vals} />;
    if (f.type === "text")    return <TextChart key={f.key} title={title} f={f} vals={vals.map(combine)} />;
    return <BarChart key={f.key} title={title} f={f} vals={vals.map(combine)} />;
  };

  const locationValues = ds?.location ? [...new Set(ds.location.raw.filter(Boolean))].sort((a, b) => a.localeCompare(b)) : [];

  const header = (
    <div className={styles.topBar}>
      <div>
        <h2 className={styles.pageTitle}>OSSE Dashboard</h2>
        <p className={styles.pageSub}>
          {ds ? <>Auswertung von <span className={styles.fileName}>{fileName}</span></> : "Auswertung eines OSSE-Register-Exports"}
        </p>
      </div>
      <div className={styles.topActions}>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden
          onChange={(e) => { loadFile(e.target.files[0]); e.target.value = ""; }} />
        <button type="button" className={styles.uploadBtn} onClick={() => fileInput.current?.click()}>
          {ds ? "Andere Datei laden" : "CSV-Export laden"}
        </button>
      </div>
    </div>
  );

  // ── Overview ─────────────────────────────────────────────────────────────
  // Patients with form g / number of filled-in forms g (episode-level forms: one per episode)
  const formCounts = (g) => {
    let patients = 0, total = 0;
    for (const p of active) { const n = g.records.get(p)?.length || 0; if (n) { patients++; total += n; } }
    return { patients, total };
  };

  const renderOverview = () => {
    const tiles = [{ num: active.length, label: "Patienten", hint: "Alle Patienten im Export" }];
    if (ds.location) {
      tiles.push({
        num: new Set(active.map((p) => valueFor(ds, ds.location, p)).filter((v) => v !== MISSING)).size,
        label: "Standorte", hint: "Standorte der Patienten",
      });
    }
    for (const g of forms) {
      const { patients, total } = formCounts(g);
      if (g.level === "episode") {
        tiles.push({ num: total, label: `${shortName(g.name)} gesamt`, hint: `Alle ausgefüllten Formulare „${shortName(g.name)}“ (zur Information)` });
        tiles.push({ num: patients, label: `Letzte ${shortName(g.name)} je Patient`, hint: "Neuestes Formular jedes Patienten – Grundlage der Statistik" });
      } else {
        tiles.push({ num: patients, label: shortName(g.name), hint: `Patienten mit ausgefülltem Formular „${shortName(g.name)}“` });
      }
    }

    // The episode date belongs to the forms filled in per episode (e.g. Kontrolle). It is
    // shown there unless the form has its own date question with the same dates.
    const episodeDate = ds.groups[0].fields.find((f) => f.label === "Episodendatum" && f.type === "date");
    const episodeForms = forms.filter((g) => g.level === "episode");
    const showEpisodeDate = (g) => episodeDate && !g.fields.some((q) => q.role === "q" && q.type === "date"
      && [...g.records.values()].flat().every((r) =>
        isoOf(valuesOf(q, r.rows)[0]) === isoOf(valuesOf(episodeDate, r.rows)[0])));

    // Standort is shown at the top, the episode date with the episode forms, the rest at the bottom
    const idFields = ds.groups[0].fields.filter((f) =>
      f !== ds.patient && f !== ds.episode && f !== ds.location && f.type !== "empty"
      && !(f === episodeDate && episodeForms.length) && !/\bid\b|nummer|number|repeat/i.test(f.label));

    return (
      <>
        <div className={styles.tileRow}>
          {tiles.map((t) => (
            <div key={t.label} className={styles.tile}>
              <span className={styles.tileNum}>{t.num}</span>
              <span className={styles.tileLabel}>{t.label}</span>
              <span className={styles.tileHint}>{t.hint}</span>
            </div>
          ))}
        </div>

        {ds.location && ds.location.type !== "empty" && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>Standorte <span className={styles.sectionCount}>n = {active.length} Patienten</span></h3>
            <ChartGrid>{[renderChart(ds.location, "_id")]}</ChartGrid>
          </section>
        )}

        {forms.map((g, gi) => {
          const questions = g.fields.filter((f) => f.role !== "status" && f.type !== "empty"
            && !HIDDEN_IN_OVERVIEW.some((re) => re.test(f.label)));
          if (!questions.length) return null;
          const { patients: withForm, total } = formCounts(g);
          return (
            <section key={g.id} className={styles.section}>
              <h3 className={styles.sectionTitle}>
                {ROMAN[gi] || gi + 1}. {g.name}
                <span className={styles.sectionCount}>n = {withForm} Patienten</span>
              </h3>
              {g.level === "episode" && withForm > 0 && (
                <p className={styles.sectionNote}>
                  Ausgewertet wird pro Patient nur das zuletzt ausgefüllte Formular ({withForm} von {total}).
                  Ältere Formulare bleiben im Export, fließen aber nicht in die Diagramme ein.
                </p>
              )}
              {withForm === 0
                ? <p className={styles.hint}>Keine Patienten mit diesem Formular für die aktuellen Filter.</p>
                : (
                  <ChartGrid>
                    {g.level === "episode" && showEpisodeDate(g) && renderChart(episodeDate, g.id)}
                    {questions.map((f) => renderChart(f))}
                  </ChartGrid>
                )}
            </section>
          );
        })}

        {idFields.length > 0 && (
          <section className={styles.section}>
            <h3 className={styles.sectionTitle}>{ds.groups[0].name} <span className={styles.sectionCount}>n = {active.length} Patienten</span></h3>
            <p className={styles.sectionNote}>Pro Patient wird die neueste Episode ausgewertet.</p>
            <ChartGrid>{idFields.map((f) => renderChart(f))}</ChartGrid>
          </section>
        )}
      </>
    );
  };

  // ── All answers (table) ──────────────────────────────────────────────────
  const renderAnswers = () => {
    // One table per form: one row per filled-in form (patient-level forms: one per
    // patient, episode-level forms: one per episode)
    const g = forms.find((x) => x.id === answersForm) || forms[0];
    if (!g) return <p className={styles.hint}>Der Export enthält keine Formulare.</p>;
    const recs = active.flatMap((p) => g.records.get(p) || []);

    // Identifier columns: patient and location, for episode-level forms also episode number and date
    const episodeDate = ds.groups[0].fields.find((f) => f.label === "Episodendatum");
    const idCols = [ds.patient, ds.location, ...(g.level === "episode" ? [ds.episode, episodeDate] : [])]
      .filter((f) => f && f.answered > 0);
    const cols = [...idCols, ...g.fields.filter((f) => f.answered > 0)];

    const pages = Math.max(1, Math.ceil(recs.length / pageSize));
    const cur = Math.min(page, pages - 1);
    const slice = recs.slice(cur * pageSize, cur * pageSize + pageSize);
    // Answers of question f in form r, several answers (multiple choice) joined with " + "
    const cell = (f, r) => valuesOf(f, r.rows).map((v) =>
      f.role === "status" ? statusText(v) : f.type === "date" && isoOf(v) ? fmtDate(isoOf(v)) : v).join(" + ");
    const colLabel = (f) => (f.role === "status" ? "Status" : f.label);

    return (
      <>
        {forms.length > 1 && (
          <div className={styles.formSwitch} role="group" aria-label="Formular">
            {forms.map((x) => {
              const n = active.reduce((a, p) => a + (x.records.get(p)?.length || 0), 0);
              return (
                <button key={x.id} type="button" aria-pressed={x === g}
                  className={x === g ? styles.formBtnActive : styles.formBtn}
                  onClick={() => { setAnswersForm(x.id); setPage(0); }}>
                  {shortName(x.name)} <span className={styles.formBtnCount}>{n}</span>
                </button>
              );
            })}
          </div>
        )}
        <div className={styles.card}>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>{cols.map((f) => <th key={f.key} title={f.label}>{colLabel(f)}</th>)}</tr>
              </thead>
              <tbody>
                {slice.map((r) => (
                  <tr key={r.rows[0]}>
                    {cols.map((f) => {
                      const v = cell(f, r);
                      return (
                        <td key={f.key} data-label={colLabel(f)} title={v}>
                          {v === "" ? <span className={styles.emptyValue}>–</span> : v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className={styles.pagination}>
            <div className={styles.perPageWrap}>
              <label className={styles.perPageLabel} htmlFor="ossePerPage">Einträge:</label>
              <select id="ossePerPage" className={styles.perPageSelect} value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}>
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className={styles.perPageLabel}>{recs.length} Formulare</span>
            </div>
            <div className={styles.pageControls}>
              <button type="button" className={styles.pageBtn} onClick={() => setPage(cur - 1)} disabled={cur === 0}>‹ Zurück</button>
              <span className={styles.pageInfo}>Seite {cur + 1} von {pages}</span>
              <button type="button" className={styles.pageBtn} onClick={() => setPage(cur + 1)} disabled={cur >= pages - 1}>Weiter ›</button>
            </div>
          </div>
        </div>
      </>
    );
  };

  // ── Rezidive (timeline per patient) ──────────────────────────────────────
  // One line per patient: Erstkontakt at day 0, then every Kontrolle, coloured by its
  // "Rezidiv" answer. Uses all Kontrollen of a patient, not only the newest.
  const renderRelapse = () => {
    const isDate = (f) => f.role === "q" && f.type === "date" && !/geburt/i.test(f.label);
    const dateOf = (g) => g.fields.find((f) => isDate(f) && /^datum$/i.test(f.label)) || g.fields.find(isDate);
    const followUp = forms.find((g) => g.level === "episode" && g.fields.some((f) => /^rezidiv$/i.test(f.label)));
    const first = forms.find((g) => g.level === "patient" && dateOf(g));
    const rezField = followUp?.fields.find((f) => /^rezidiv$/i.test(f.label));
    const fuDate = followUp && (dateOf(followUp) || ds.groups[0].fields.find((f) => f.label === "Episodendatum"));
    if (!followUp || !fuDate) {
      return <p className={styles.hint}>Der Export enthält kein Formular mit der Frage „Rezidiv“ und einem Datum.</p>;
    }

    const days = (from, to) => Math.round((Date.parse(to) - Date.parse(from)) / 86400000);
    const items = active.map((p) => {
      const visits = (followUp.records.get(p) || [])
        .map((r) => ({ iso: isoOf(valuesOf(fuDate, r.rows)[0]), rez: valuesOf(rezField, r.rows)[0] || "" }))
        .filter((v) => v.iso)
        .sort((a, b) => a.iso.localeCompare(b.iso));
      const firstRec = first && recordFor(ds, first.id, p);
      const startIso = (firstRec && isoOf(valuesOf(dateOf(first), firstRec.rows)[0])) || visits[0]?.iso || null;
      const withDays = visits.map((v) => ({ ...v, day: startIso ? days(startIso, v.iso) : 0 }));
      const firstRez = withDays.find((v) => /^ja$/i.test(v.rez));
      // Current status = Rezidiv answer of the newest Kontrolle
      const latest = withDays[withDays.length - 1];
      const current = !latest ? "none" : /^ja$/i.test(latest.rez) ? "yes" : /^nein$/i.test(latest.rez) ? "no" : "unknown";
      return {
        pid: p, startIso, visits: withDays, firstRez, current,
        location: ds.location ? valueFor(ds, ds.location, p) : "",
      };
    });
    // Current Rezidiv first, then no Rezidiv, not answered, no Kontrolle; then by PID
    const order = { yes: 0, no: 1, unknown: 2, none: 3 };
    items.sort((a, b) => order[a.current] - order[b.current]
      || String(a.pid).localeCompare(String(b.pid), "de", { numeric: true }));

    const withRez = items.filter((it) => it.current === "yes").length;
    const everRez = items.filter((it) => it.firstRez).length;
    const BADGE = {
      yes: [styles.tlBadgeYes, "Ja"], no: [styles.tlBadgeNo, "Nein"],
      unknown: [styles.tlBadgeNone, "Nicht angegeben"], none: [styles.tlBadgeNone, "Keine Kontrolle"],
    };

    const pages = Math.max(1, Math.ceil(items.length / pageSize));
    const cur = Math.min(tlPage, pages - 1);
    const slice = items.slice(cur * pageSize, cur * pageSize + pageSize);
    const dotClass = (rez) => (/^ja$/i.test(rez) ? styles.tlDotYes : /^nein$/i.test(rez) ? styles.tlDotNo : styles.tlDotUnknown);

    return (
      <>
        <p className={styles.sectionNote}>
          Aktuelles Rezidiv (letzte {shortName(followUp.name)}): <b>{withRez}</b> von {items.length} Patienten ({pct(withRez, items.length)}%) ·
          mindestens ein Rezidiv im Verlauf: <b>{everRez}</b>.
          Jede Linie beginnt beim {shortName(first?.name || "Erstkontakt")} (Tag 0) und endet bei der letzten {shortName(followUp.name)};
          die Zahlen geben die Tage seit dem {shortName(first?.name || "Erstkontakt")} an.
        </p>
        <div className={styles.tlLegend}>
          <span><span className={`${styles.tlDot} ${styles.tlDotStart}`} /> {shortName(first?.name || "Erstkontakt")}</span>
          <span><span className={`${styles.tlDot} ${styles.tlDotNo}`} /> Kontrolle ohne Rezidiv</span>
          <span><span className={`${styles.tlDot} ${styles.tlDotYes}`} /> Kontrolle mit Rezidiv</span>
          <span><span className={`${styles.tlDot} ${styles.tlDotUnknown}`} /> Rezidiv nicht angegeben</span>
        </div>
        <div className={styles.card}>
          <div className={styles.tlList}>
            <div className={`${styles.tlRow} ${styles.tlAxisRow}`}>
              <span className={styles.tlInfo}>Patient</span>
              <span>Verlauf (Tage seit {shortName(first?.name || "Erstkontakt")})</span>
              <span className={styles.tlBadgeHead}>Aktuelles Rezidiv</span>
            </div>
            {slice.map((it) => {
              // Individual scale: the line runs from day 0 to this patient's last Kontrolle
              const span = Math.max(1, ...it.visits.map((v) => v.day));
              const x = (d) => `${(Math.min(Math.max(0, d), span) / span) * 100}%`;
              const points = [
                ...(it.startIso ? [{ day: 0, cls: styles.tlDotStart, title: `${shortName(first?.name || "Erstkontakt")}: ${fmtDate(it.startIso)}` }] : []),
                ...it.visits.map((v) => ({
                  day: v.day, cls: dotClass(v.rez),
                  title: `${shortName(followUp.name)} ${fmtDate(v.iso)} · Tag ${v.day} · Rezidiv: ${v.rez || "nicht angegeben"}`,
                })),
              ];
              // Day labels under the points; skip a label that would overlap the previous one
              let lastLabel = -Infinity;
              const labelled = points.map((pt) => {
                const pos = Math.max(0, pt.day) / span;
                const show = pos - lastLabel >= 0.1 || pt === points[points.length - 1];
                if (show) lastLabel = pos;
                return show;
              });
              const [badgeCls, badgeText] = BADGE[it.current];
              return (
                <div key={it.pid} className={styles.tlRow}>
                  <span className={styles.tlInfo}>
                    <b>PID {it.pid}</b>
                    {it.location && it.location !== MISSING && <span className={styles.tlLocation}>{it.location}</span>}
                  </span>
                  <div className={styles.tlTrack}>
                    {it.visits.length > 0 && <span className={styles.tlLine} />}
                    {points.map((pt, k) => (
                      <span key={k}>
                        <span className={`${styles.tlDot} ${pt.cls}`} style={{ left: x(pt.day) }} title={pt.title} />
                        {labelled[k] && (
                          <span className={`${styles.tlDayLabel}${pt.day / span > 0.85 && pt.day > 0 ? " " + styles.tlTickEnd : ""}`}
                            style={{ left: x(pt.day) }}>
                            {k === points.length - 1 && pt.day > 0 ? `${pt.day} Tage` : pt.day}
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                  <span className={`${styles.tlBadge} ${badgeCls}`}
                    title={it.firstRez ? `Erstes Rezidiv am ${fmtDate(it.firstRez.iso)} (Tag ${it.firstRez.day})` : undefined}>
                    <span className={styles.tlBadgePrefix}>Aktuelles Rezidiv: </span>{badgeText}
                  </span>
                </div>
              );
            })}
          </div>
          <div className={styles.pagination}>
            <div className={styles.perPageWrap}>
              <label className={styles.perPageLabel} htmlFor="osseTlPerPage">Einträge:</label>
              <select id="osseTlPerPage" className={styles.perPageSelect} value={pageSize}
                onChange={(e) => { setPageSize(Number(e.target.value)); setTlPage(0); }}>
                {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              <span className={styles.perPageLabel}>{items.length} Patienten</span>
            </div>
            <div className={styles.pageControls}>
              <button type="button" className={styles.pageBtn} onClick={() => setTlPage(cur - 1)} disabled={cur === 0}>‹ Zurück</button>
              <span className={styles.pageInfo}>Seite {cur + 1} von {pages}</span>
              <button type="button" className={styles.pageBtn} onClick={() => setTlPage(cur + 1)} disabled={cur >= pages - 1}>Weiter ›</button>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className={styles.page}>
      <div className={styles.content}>
        {header}
        {error && <p className={styles.errorBox}>{error}</p>}

        {!ds ? (
          <div className={styles.emptyCard}>
            <p className={styles.emptyTitle}>Register-Export laden, um zu starten</p>
            <p className={styles.emptyText}>
              Wählen Sie die aus der OSSE-Registerplattform exportierte CSV-Datei aus oder ziehen Sie sie auf diese Seite.
              Formulare, Fragen und Diagramme werden direkt aus der Datei erstellt.
            </p>
            <button type="button" className={styles.uploadBtn} onClick={() => fileInput.current?.click()}>CSV-Datei auswählen</button>
            <p className={styles.emptyNote}>Die Datei wird nur in Ihrem Browser gelesen und nicht hochgeladen.</p>
          </div>
        ) : (
          <>
            <div className={styles.filterBar}>
              {ds.location && (
                <label className={styles.filterLabel}>
                  Standort
                  <select
                    className={styles.filterSelect}
                    value={location}
                    onChange={(e) => { setLocation(e.target.value); setPage(0); setTlPage(0); }}
                  >
                    <option value="">Alle Standorte</option>
                    {locationValues.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
              )}
            </div>

            <div className={styles.tabs} role="tablist">
              <button type="button" role="tab" aria-selected={tab === "overview"} className={tab === "overview" ? styles.tabActive : styles.tab} onClick={() => setTab("overview")}>Übersicht</button>
              <button type="button" role="tab" aria-selected={tab === "answers"} className={tab === "answers" ? styles.tabActive : styles.tab} onClick={() => setTab("answers")}>Alle Antworten</button>
              <button type="button" role="tab" aria-selected={tab === "relapse"} className={tab === "relapse" ? styles.tabActive : styles.tab} onClick={() => setTab("relapse")}>Rezidive</button>
            </div>

            {tab === "overview" ? renderOverview() : tab === "answers" ? renderAnswers() : renderRelapse()}
          </>
        )}

        <div className={styles.actionsRow}>
          <button type="button" className={styles.primaryBtn} onClick={() => navigate(-1)}>Zurück</button>
        </div>
      </div>

      {dragging && <div className={styles.dropOverlay}>CSV-Export hier ablegen, um ihn zu laden</div>}
    </div>
  );
}
